import { SetAssetPracticeTask } from "./SetAssetPracticeTask.js";
import { useEffect, useRef, useState } from "react";
import { getStaffRepertoireMaterials, REPERTOIRE_STATUS_LABELS, type RepertoireEntry, type RepertoireMaterial } from "./repertoire.js";
import type { LessonResourceMatch } from "./LessonResourceEditor.js";
import { useAsync } from "./useAsync.js";
import type { LessonRepertoireSelection, LessonRepertoireSelectionResult } from "./repertoire.js";

export function LessonRepertoirePicker({ studentId, current, loading, error, onRetry, onAddPiece, onAddAsset, onAssign, onVisibilityChange, onBusyChange, loadMaterials = getStaffRepertoireMaterials, disabled = false, selectionRequest, practiceDomain = "private", onPracticeChanged }: {
  studentId: string;
  current: RepertoireEntry[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onAddPiece?: (title: string) => void;
  onAddAsset?: (resource: LessonResourceMatch, key: string) => Promise<void>;
  loadMaterials?: typeof getStaffRepertoireMaterials;
  onAssign?: (selection: LessonRepertoireSelection, key: string) => Promise<LessonRepertoireSelectionResult>;
  onVisibilityChange?: (repertoireId: string, visible: boolean) => Promise<void>;
  onBusyChange: (busy: boolean) => void;
  disabled?: boolean;
  selectionRequest?: { entryId: string };
  practiceDomain?: "private" | "school";
  onPracticeChanged?: () => void;
}) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const [addedPieces, setAddedPieces] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const selectionPanel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (selectionRequest) { setSelected(selectionRequest.entryId); setQuery(""); }
  }, [selectionRequest]);
  const piece = current.find((entry) => entry.id === selected);
  useEffect(() => {
    if (selectionRequest?.entryId === piece?.id) selectionPanel.current?.scrollIntoView?.({ block: "start" });
  }, [selectionRequest, piece?.id]);
  const words = query.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
  const matches = current.filter((entry) => {
    const text = [entry.title, entry.composer, entry.workOrCollection, entry.instrument].filter(Boolean).join(" ").toLocaleLowerCase();
    return words.every((word) => text.includes(word));
  });
  return (
    <section className="lesson-repertoire-picker" aria-label="Choose current repertoire">
      <h3>Current repertoire</h3>
      {current.length > 0 && <label className="lesson-tool-field"><span>Search current repertoire</span>
        <input type="search" value={query} onChange={(event) => setQuery(event.target.value)} disabled={busy} placeholder="Piece, artist, collection or instrument" />
      </label>}
      {loading && <p role="status">Loading current repertoire…</p>}
      {error && <div className="error" role="alert">{error} <button type="button" className="quiet" onClick={onRetry}>Try again</button></div>}
      {!loading && !error && <>
        {matches.length === 0 && <p className="empty">{current.length ? "No current pieces match your search." : "Nothing on the current repertoire yet."}</p>}
        <div className="lesson-repertoire-results">
          {matches.map((entry) => <button type="button" key={entry.id} className={`quiet lesson-repertoire-piece${entry.id === selected ? " is-selected" : ""}`}
            aria-pressed={entry.id === selected} disabled={busy || disabled} onClick={() => setSelected(entry.id)}>
            <strong>{entry.title}</strong><span className="small muted">{[entry.composer, REPERTOIRE_STATUS_LABELS[entry.status]].filter(Boolean).join(" · ")}</span>
          </button>)}
        </div>
        {piece && <div className="lesson-repertoire-selection" ref={selectionPanel}>
          <h4>{piece.title}</h4>
          {onAddPiece && <button type="button" disabled={disabled || busy || addedPieces.includes(piece.id)} onClick={() => {
            onAddPiece(piece.title); setAddedPieces((previous) => [...previous, piece.id]);
          }}>{addedPieces.includes(piece.id) ? "Piece added to notes" : "Add piece to notes"}</button>}
          <RepertoireAssets key={piece.id} studentId={studentId} piece={piece} disabled={disabled || busy}
            practiceDomain={practiceDomain} onPracticeChanged={onPracticeChanged}
            onAdd={onAddAsset} onAssign={onAssign} onVisibilityChange={onVisibilityChange} loadMaterials={loadMaterials} onBusyChange={(value) => { setBusy(value); onBusyChange(value); }} />
        </div>}
      </>}
    </section>
  );
}

function RepertoireAssets({ studentId, piece, disabled, onAdd, onAssign, onVisibilityChange, onBusyChange, loadMaterials, practiceDomain, onPracticeChanged }: {
  studentId: string; piece: RepertoireEntry; disabled: boolean;
  onAdd?: (resource: LessonResourceMatch, key: string) => Promise<void>;
  loadMaterials: typeof getStaffRepertoireMaterials;
  onAssign?: (selection: LessonRepertoireSelection, key: string) => Promise<LessonRepertoireSelectionResult>;
  onVisibilityChange?: (repertoireId: string, visible: boolean) => Promise<void>;
  onBusyChange: (busy: boolean) => void;
  practiceDomain: "private" | "school";
  onPracticeChanged?: () => void;
}) {
  const materials = useAsync(() => loadMaterials(studentId, piece.id), [studentId, piece.id, loadMaterials]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [added, setAdded] = useState<string[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  const keys = useRef(new Map<string, string>());
  const [selected, setSelected] = useState<string[]>([]);
  const [shared, setShared] = useState<string[]>([]);
  const [songTask, setSongTask] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [familyVisible, setFamilyVisible] = useState(piece.familyVisible);
  const supported = (asset: RepertoireMaterial) => asset.kind !== "hub_asset" || (asset.hubProvider === "groove" && Boolean(asset.hubAssetId));
  useEffect(() => {
    if (materials.data) {
      const current = materials.data.filter(asset => asset.shared).map(asset => asset.id);
      setShared(current); setSelected(current);
    }
  }, [materials.data]);
  useEffect(() => { setFamilyVisible(piece.familyVisible); }, [piece.familyVisible]);
  async function changeVisibility() {
    if (!onVisibilityChange || disabled || busyId) return;
    const visible = familyVisible === false;
    setBusyId("visibility"); setProblem(null); setNotice(null); onBusyChange(true);
    try {
      await onVisibilityChange(piece.id, visible);
      setFamilyVisible(visible);
      setNotice(visible ? "Song and selected assets are visible to the student. Use practice assignment to restore withdrawn tasks." : "Song and assets are hidden from the student. Unfinished song and asset tasks are withdrawn; selection and practice history are kept.");
    } catch (cause) { setProblem(cause instanceof Error ? cause.message : "Could not change song visibility. Try again."); }
    finally { setBusyId(null); onBusyChange(false); }
  }
  async function assign(practice: boolean) {
    if (!onAssign || disabled || busyId) return;
    const input = { repertoireId: piece.id, resourceIds: [...selected].sort(), expectedSharedResourceIds: [...shared].sort(), practice, songTask: practice && songTask };
    const signature = JSON.stringify(input);
    const key = keys.current.get(signature) ?? crypto.randomUUID(); keys.current.set(signature, key);
    setBusyId("assignment"); setProblem(null); setNotice(null); onBusyChange(true);
    try {
      const result = await onAssign(input, key);
      keys.current.delete(signature);
      setShared(result.resourceIds);
      if (result.familyVisible !== undefined) setFamilyVisible(result.familyVisible);
      const withdrawn = result.withdrawnTaskIds?.length ?? 0;
      setNotice(`Shared ${result.resourceIds.length} ${result.resourceIds.length === 1 ? "asset" : "assets"} with the student${practice ? `; ${result.taskIds.length} practice ${result.taskIds.length === 1 ? "task" : "tasks"} assigned` : ""}.${withdrawn ? ` ${withdrawn} asset ${withdrawn === 1 ? "task is" : "tasks are"} no longer assigned; practice history is kept.` : ""}`);
    } catch (cause) { setProblem(cause instanceof Error ? cause.message : "Could not share that selection. Try again."); }
    finally { setBusyId(null); onBusyChange(false); }
  }
  async function add(asset: RepertoireMaterial) {
    if (!onAdd || disabled || busyId) return;
    const groove = asset.kind === "hub_asset" && asset.hubProvider === "groove" && asset.hubAssetId;
    const key = keys.current.get(asset.id) ?? crypto.randomUUID();
    keys.current.set(asset.id, key);
    setBusyId(asset.id); setProblem(null); onBusyChange(true);
    try {
      await onAdd({ kind: groove ? "groove" : "repertoire_resource", id: groove || asset.id, title: asset.title,
        typeLabel: groove ? "Drum groove" : asset.presentationKind, repertoireId: piece.id }, key);
      setAdded((previous) => [...previous, asset.id]);
    } catch (cause) { setProblem(cause instanceof Error ? cause.message : "Could not add that asset. Try again."); }
    finally { setBusyId(null); onBusyChange(false); }
  }
  return <div className="lesson-repertoire-assets">
    <h4>Individual assets</h4>
    <p className="small muted">{onAssign ? "Tick the assets the student should have. Unticked assets are unshared and their automatically created asset tasks are withdrawn; practice history is kept." : "Choose the files, links or Grooves to include in this lesson. Grooves also become practice tasks."}</p>
    {materials.loading && <p role="status">Loading assets…</p>}
    {materials.error && <div className="error" role="alert">{materials.error} <button type="button" className="quiet" onClick={materials.reload}>Try again</button></div>}
    {problem && <div className="error" role="alert">{problem} {onAssign && <button type="button" className="quiet" disabled={disabled || busyId !== null} onClick={() => { setProblem(null); setNotice(null); materials.reload(); }}>Reload assets</button>}</div>}
    {notice && <p role="status">{notice}</p>}
    {onVisibilityChange && <div className="lesson-repertoire-assignment">
      <p className="small muted">{familyVisible === false ? "This song is hidden from the student." : "This song is visible in the student’s repertoire, even with no shared assets."} Hiding it also withdraws unfinished song and asset tasks; practice history is kept.</p>
      <button type="button" className="quiet" disabled={disabled || busyId !== null} onClick={() => void changeVisibility()}>{busyId === "visibility" ? "Saving…" : familyVisible === false ? "Show song to student" : "Hide song from student"}</button>
    </div>}
    {!materials.loading && !materials.error && <>
      {!materials.data?.length && <p className="empty">This piece has no linked assets yet.</p>}
      {onAssign && Boolean(materials.data?.length) && <div className="lesson-repertoire-bulk-actions">
        <button type="button" className="quiet small" disabled={disabled || busyId !== null} onClick={() => setSelected((materials.data ?? []).filter(supported).map(asset => asset.id))}>Select all</button>
        <button type="button" className="quiet small" disabled={disabled || busyId !== null} onClick={() => setSelected([])}>Clear selection</button>
        <span className="small muted">{selected.length} selected</span>
      </div>}
      {materials.data?.map((asset) => {
        const available = supported(asset);
        return <div className="lesson-repertoire-asset" key={asset.id}>
          <label className="lesson-repertoire-choice">
            {onAssign && <input type="checkbox" aria-label={`Select ${asset.title}`} checked={selected.includes(asset.id)} disabled={disabled || busyId !== null || !available}
              onChange={event => { const checked = event.target.checked; setSelected(current => checked ? [...current, asset.id] : current.filter(id => id !== asset.id)); }} />}
            <span><strong>{asset.title}</strong><span className="small muted">{asset.kind === "hub_asset" ? "Groove" : asset.presentationKind.toUpperCase()}{shared.includes(asset.id) ? " · Shared" : ""}</span>
            {asset.description && <span className="small muted">{asset.description}</span>}</span></label>
          {shared.includes(asset.id) && familyVisible !== false && available && <SetAssetPracticeTask target={{ domain: practiceDomain, learnerId: studentId }}
            source={{ kind: "repertoire_asset", repertoireId: piece.id, id: asset.id }} assetTitle={asset.title}
            disabled={disabled || busyId !== null} onBusyChange={onBusyChange} onSaved={() => { materials.reload(); onPracticeChanged?.(); }} />}
          {onAdd && <button type="button" className="quiet" aria-label={`Add ${asset.title} to lesson`} disabled={disabled || busyId !== null || added.includes(asset.id) || !available} onClick={() => void add(asset)}>
            {busyId === asset.id ? "Adding…" : added.includes(asset.id) ? "Added to lesson" : available ? onAssign ? "Add to notes" : "Add to lesson" : "Unavailable"}
          </button>}
        </div>;
      })}
      {onAssign && <div className="lesson-repertoire-assignment">
        {familyVisible === false && <p className="small muted">Sharing an asset or assigning the song makes it visible to the student. Sharing an empty selection keeps it hidden.</p>}
        <label className="lesson-repertoire-choice"><input type="checkbox" checked={songTask} disabled={disabled || busyId !== null} onChange={event => setSongTask(event.target.checked)} /><span>Add a practice task for the song</span></label>
        <p className="small muted">Practice adds one task per selected asset. Previously withdrawn tasks are restored with their progress kept. The song can have its own task too.</p>
        <div className="lesson-repertoire-bulk-actions">
          <button type="button" className="quiet" disabled={disabled || busyId !== null} onClick={() => void assign(false)}>{busyId === "assignment" ? "Saving…" : "Share selected assets"}</button>
          <button type="button" disabled={disabled || busyId !== null || (!selected.length && !songTask)} onClick={() => void assign(true)}>Share and add practice tasks</button>
        </div>
      </div>}
    </>}
  </div>;
}
